import React, { useEffect, useState } from 'react';
import { INK } from '../../Utils/styleTokens';
import {
  getDiscoverySettings,
  saveDiscoverySettings,
  getPlatformConfig,
  getAdvancedConfig,
  saveAdvancedConfig,
  getDbPortDefaults,
  addDbPortDefault,
  deleteDbPortDefault,
  sendTestEmail,
} from '../api';

// Deployment default row cap, pulled from the read-only platform-config items (label contains
// "max rows"). Returns the value as a string, or null when platform-config hasn't surfaced it.
const deploymentRowCapFrom = (platformCfg) => {
  const hit = (platformCfg?.platform || []).find(
    (it) => it && typeof it.label === 'string' && it.label.toLowerCase().includes('max rows'),
  );
  return hit != null ? String(hit.value) : null;
};

// Maps the normalized advanced-config SMTP fields into the card's editable state. Nulls
// become '' for text inputs (= "no override"); enabled/tlsMode stay null for their
// "Default" select option.
const smtpStateFrom = (adv) => ({
  enabled: adv.smtpEnabled != null ? adv.smtpEnabled : null,
  host: adv.smtpHost != null ? String(adv.smtpHost) : '',
  port: adv.smtpPort != null ? String(adv.smtpPort) : '',
  tlsMode: adv.smtpTlsMode != null ? adv.smtpTlsMode : null,
  username: adv.smtpUsername != null ? String(adv.smtpUsername) : '',
  fromAddress: adv.smtpFromAddress != null ? String(adv.smtpFromAddress) : '',
  passwordSet: !!adv.smtpPasswordSet,
});

// One domain per line (commas tolerated) -> array. Client-side just splits/trims/drops
// empties; the server does the real normalization (lowercase, leading '@', dotless, dedupe).
const parseDomainList = (text) => String(text)
  .split(/[\n,]+/)
  .map((s) => s.trim())
  .filter(Boolean);

// Muted "Default: <value>" hint under an SMTP field, sourced from the deployment env defaults.
const EnvDefaultHint = ({ value }) => (
  value != null && value !== ''
    ? <div style={{ fontSize: 11, color: INK[500], marginTop: 4 }}>Default: {String(value)}</div>
    : null
);

// Settings screen (Advanced Config). Landscape refresh lives in c2-manager; the global sampling
// row cap is stored on the AdvancedConfigSettings singleton and enforced by the discovery worker.
// Other YAML/env knobs are shown read-only at the bottom.
const SettingsScreen = ({ canEdit, user }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Landscape refresh scheduling. Default interval mirrors the c2-manager backend default
  // (c2.discovery.scheduled.interval-ms = 3600000ms = 60 min); overwritten by the GET on load.
  const [disc, setDisc] = useState({ enabled: false, intervalMinutes: 60, minIntervalMinutes: 3 });
  const [savingDisc, setSavingDisc] = useState(false);
  const [discMsg, setDiscMsg] = useState(null);

  // Global sampling row cap (AdvancedConfigSettings.maxRowsPerTable). '' = not set → deployment
  // default. Sibling advanced-config fields are round-tripped on save (backend does full replace).
  const [maxRows, setMaxRows] = useState('');
  const [advSiblings, setAdvSiblings] = useState({ entityLevels: {}, entityTypeToAlgorithm: {}, entityOrder: [] });
  const [savingMax, setSavingMax] = useState(false);
  const [maxMsg, setMaxMsg] = useState(null);

  // Embedded-structure scan (c2-discover-api docs/file-support-matrix.md §4c). Tri-state:
  // null = unset -> the discover worker's DISCOVER_EMBEDDED_LEAVES default (on); true/false force.
  // Kept as null rather than coerced so an untouched install keeps shipped behavior.
  const [scanEmbedded, setScanEmbedded] = useState(null);
  const [savingEmbedded, setSavingEmbedded] = useState(false);
  const [embeddedMsg, setEmbeddedMsg] = useState(null);

  // Profile / Schema Metadata feature toggle (AdvancedConfigSettings.profileEnabled).
  // Re-enabled: the metadata/profile service now lives in c2-discover-api and is proxied
  // through platform-api, so the earlier "backend gaps" dark ship no longer applies. Admins
  // turn the "Schema Metadata" nav item on/off here (it now defaults on backend-side). Sibling
  // saves round-trip this field so a full-replace PUT never silently pins it back off.
  const [profileEnabled, setProfileEnabled] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  // Data-source Import/Export gate (AdvancedConfigSettings.dataSourceImportExportEnabled) — C2D-1000/998.
  const [importExportEnabled, setImportExportEnabled] = useState(false);
  const [savingImportExport, setSavingImportExport] = useState(false);
  const [importExportMsg, setImportExportMsg] = useState(null);

  // Discovery-coverage knobs (V28): profile-before-discover freshness window, per-column
  // profile stats, sparse-column refill, and the post-discovery verify sweep. All tri-state /
  // nullable — null defers to the worker deployment default. saveAdvancedConfig merges over
  // the stored config, so this card only sends its own fields.
  const [coverage, setCoverage] = useState({
    profileFreshnessHours: null,
    columnStatsEnabled: null,
    refillEnabled: null,
    refillSparseThreshold: null,
    verifySweepEnabled: null,
    autofanoutMaxChildrenPerRun: null,
    autofanoutMaxConcurrentChildren: null,
  });
  const [savingCoverage, setSavingCoverage] = useState(false);
  const [coverageMsg, setCoverageMsg] = useState(null);

  // Test data subsets (contract 3.0, V61): the FANOUT threshold of the isolation report — a rule
  // that returns more rows than this for one source value is flagged. null = the default (1,000).
  const [tdmFanout, setTdmFanout] = useState(null);
  const [savingTdm, setSavingTdm] = useState(false);
  const [tdmMsg, setTdmMsg] = useState(null);

  // Email (SMTP) overrides. Per-field null / '' = "use the deployment env default"; the
  // env defaults themselves come back read-only in smtpEnvDefaults for the "Default: …"
  // hints. The password is write-only: smtpPassword is included in the save payload ONLY
  // when the admin typed one, and smtpPasswordClear drops the stored override.
  const [smtp, setSmtp] = useState({
    enabled: null, host: '', port: '', tlsMode: null, username: '', fromAddress: '', passwordSet: false,
  });
  const [smtpEnvDefaults, setSmtpEnvDefaults] = useState({});
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpClearPassword, setSmtpClearPassword] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [smtpMsg, setSmtpMsg] = useState(null);
  // Test email. Prefilled with the signed-in admin's address when available.
  const [testTo, setTestTo] = useState(user && user.email ? user.email : '');
  const [sendingTest, setSendingTest] = useState(false);
  // null, or { ok: boolean, text: string } — the 422 error text is shown verbatim.
  const [testMsg, setTestMsg] = useState(null);

  // User email-domain policy (account policy, separate from SMTP). Edited as one-per-line
  // text; saved as arrays. Empty = no restriction; blacklist wins server-side.
  const [domains, setDomains] = useState({ whitelist: '', blacklist: '' });
  const [savingDomains, setSavingDomains] = useState(false);
  const [domainsMsg, setDomainsMsg] = useState(null);

  // Read-only platform config (YAML/env knobs), grouped by service. Also the source of the
  // deployment default row cap shown as reference next to the global field.
  const [platformCfg, setPlatformCfg] = useState({ platform: [], manager: [] });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [d, adv, pcfg] = await Promise.all([
          getDiscoverySettings().catch(() => null),
          getAdvancedConfig().catch(() => ({ maxRowsPerTable: null, entityLevels: {}, entityTypeToAlgorithm: {} })),
          getPlatformConfig().catch(() => ({ platform: [], manager: [] })),
        ]);
        if (!alive) return;
        if (d) setDisc({
          enabled: !!d.enabled,
          intervalMinutes: d.intervalMinutes ?? 60,
          minIntervalMinutes: d.minIntervalMinutes ?? 3,
        });
        const cfg = pcfg || { platform: [], manager: [] };
        setPlatformCfg(cfg);
        setAdvSiblings({
          entityLevels: adv.entityLevels || {},
          entityTypeToAlgorithm: adv.entityTypeToAlgorithm || {},
          entityOrder: Array.isArray(adv.entityOrder) ? adv.entityOrder : [],
        });
        setScanEmbedded(adv.scanEmbeddedStructures != null ? adv.scanEmbeddedStructures : null);
        setProfileEnabled(adv.profileEnabled !== false);
        setImportExportEnabled(adv.dataSourceImportExportEnabled === true);
        setCoverage({
          profileFreshnessHours: adv.profileFreshnessHours != null ? adv.profileFreshnessHours : null,
          columnStatsEnabled: adv.columnStatsEnabled != null ? adv.columnStatsEnabled : null,
          refillEnabled: adv.refillEnabled != null ? adv.refillEnabled : null,
          refillSparseThreshold: adv.refillSparseThreshold != null ? adv.refillSparseThreshold : null,
          verifySweepEnabled: adv.verifySweepEnabled != null ? adv.verifySweepEnabled : null,
          autofanoutMaxChildrenPerRun:
            adv.autofanoutMaxChildrenPerRun != null ? adv.autofanoutMaxChildrenPerRun : null,
          autofanoutMaxConcurrentChildren:
            adv.autofanoutMaxConcurrentChildren != null ? adv.autofanoutMaxConcurrentChildren : null,
        });
        setTdmFanout(adv.tdmFanoutThreshold != null ? adv.tdmFanoutThreshold : null);
        setSmtp(smtpStateFrom(adv));
        setSmtpEnvDefaults(adv.smtpEnvDefaults && typeof adv.smtpEnvDefaults === 'object' ? adv.smtpEnvDefaults : {});
        setDomains({
          whitelist: (adv.emailDomainWhitelist || []).join('\n'),
          blacklist: (adv.emailDomainBlacklist || []).join('\n'),
        });
        // Prefill: saved override wins; otherwise seed the box with the deployment default so the
        // operator sees a concrete, editable number instead of a blank box. The 'Clear' button
        // reverts to a true no-override (blank) state.
        if (adv.maxRowsPerTable != null) {
          setMaxRows(String(adv.maxRowsPerTable));
        } else {
          const def = deploymentRowCapFrom(cfg);
          setMaxRows(def != null ? def : '');
        }
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load settings');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const saveDiscovery = async () => {
    setSavingDisc(true);
    setDiscMsg(null);
    try {
      const saved = await saveDiscoverySettings(disc.enabled, Number(disc.intervalMinutes));
      setDisc((prev) => ({ ...prev, ...saved }));
      setDiscMsg('Saved.');
    } catch (e) {
      setDiscMsg(e?.message || 'Save failed');
    } finally {
      setSavingDisc(false);
    }
  };

  const saveMaxRows = async () => {
    setSavingMax(true);
    setMaxMsg(null);
    try {
      const trimmed = String(maxRows).trim();
      const value = trimmed === '' ? null : Number(trimmed);
      const res = await saveAdvancedConfig({
        entityLevels: advSiblings.entityLevels,
        entityTypeToAlgorithm: advSiblings.entityTypeToAlgorithm,
        entityOrder: advSiblings.entityOrder,
        maxRowsPerTable: value,
        scanEmbeddedStructures: scanEmbedded,
        profileEnabled,
      });
      setMaxRows(res.maxRowsPerTable != null ? String(res.maxRowsPerTable) : '');
      setMaxMsg(value == null
        ? 'Cleared — discovery now uses the deployment default.'
        : 'Saved. Applies to all discovery runs, install-wide.');
    } catch (e) {
      setMaxMsg(e?.message || 'Save failed');
    } finally {
      setSavingMax(false);
    }
  };

  // `value` is the tri-state: null clears the override (worker default), true/false force it.
  const saveScanEmbedded = async (value) => {
    setSavingEmbedded(true);
    setEmbeddedMsg(null);
    try {
      const res = await saveAdvancedConfig({
        entityLevels: advSiblings.entityLevels,
        entityTypeToAlgorithm: advSiblings.entityTypeToAlgorithm,
        entityOrder: advSiblings.entityOrder,
        maxRowsPerTable: maxRows.trim() === '' ? null : Number(maxRows.trim()),
        scanEmbeddedStructures: value,
        profileEnabled,
      });
      const saved = res.scanEmbeddedStructures != null ? res.scanEmbeddedStructures : null;
      setScanEmbedded(saved);
      setEmbeddedMsg(saved == null
        ? 'Cleared — discovery now uses the deployment default.'
        : (saved
          ? 'Saved. Embedded JSON/XML column values are scanned field-by-field.'
          : 'Saved. Embedded JSON/XML column values are scanned as plain text only.'));
    } catch (e) {
      setEmbeddedMsg(e?.message || 'Save failed');
    } finally {
      setSavingEmbedded(false);
    }
  };

  // Persist the Profile feature toggle. Full-replace backend, so round-trip the sibling
  // fields (entity levels, algorithm map, row cap, embedded scan) unchanged.
  const saveProfile = async (nextEnabled) => {
    setSavingProfile(true);
    setProfileMsg(null);
    try {
      const trimmed = String(maxRows).trim();
      const res = await saveAdvancedConfig({
        entityLevels: advSiblings.entityLevels,
        entityTypeToAlgorithm: advSiblings.entityTypeToAlgorithm,
        entityOrder: advSiblings.entityOrder,
        maxRowsPerTable: trimmed === '' ? null : Number(trimmed),
        scanEmbeddedStructures: scanEmbedded,
        profileEnabled: nextEnabled,
      });
      setProfileEnabled(res.profileEnabled !== false);
      setProfileMsg('Saved. Reload to update the navigation menu.');
    } catch (e) {
      setProfileMsg(e?.message || 'Save failed');
    } finally {
      setSavingProfile(false);
    }
  };

  // Persist the data-source Import/Export gate. saveAdvancedConfig merges over the stored config,
  // so only this one flag changes; sibling cards are untouched (C2D-1000/998).
  const saveImportExport = async (nextEnabled) => {
    setSavingImportExport(true);
    setImportExportMsg(null);
    try {
      const res = await saveAdvancedConfig({ dataSourceImportExportEnabled: nextEnabled });
      setImportExportEnabled(res.dataSourceImportExportEnabled === true);
      setImportExportMsg('Saved. Reload to update the Data Sources page.');
    } catch (e) {
      setImportExportMsg(e?.message || 'Save failed');
    } finally {
      setSavingImportExport(false);
    }
  };

  // Persist the Discovery coverage card. saveAdvancedConfig merges these over the stored
  // config, so sibling fields from the other cards are untouched.
  const saveCoverage = async () => {
    setSavingCoverage(true);
    setCoverageMsg(null);
    try {
      const res = await saveAdvancedConfig({
        profileFreshnessHours: coverage.profileFreshnessHours !== null
          && String(coverage.profileFreshnessHours).trim() !== ''
          ? Number(coverage.profileFreshnessHours) : null,
        columnStatsEnabled: coverage.columnStatsEnabled,
        refillEnabled: coverage.refillEnabled,
        refillSparseThreshold: coverage.refillSparseThreshold !== null
          && String(coverage.refillSparseThreshold).trim() !== ''
          ? Number(coverage.refillSparseThreshold) : null,
        verifySweepEnabled: coverage.verifySweepEnabled,
        autofanoutMaxChildrenPerRun: coverage.autofanoutMaxChildrenPerRun !== null
          && String(coverage.autofanoutMaxChildrenPerRun).trim() !== ''
          ? Number(coverage.autofanoutMaxChildrenPerRun) : null,
        autofanoutMaxConcurrentChildren: coverage.autofanoutMaxConcurrentChildren !== null
          && String(coverage.autofanoutMaxConcurrentChildren).trim() !== ''
          ? Number(coverage.autofanoutMaxConcurrentChildren) : null,
      });
      setCoverage({
        profileFreshnessHours: res.profileFreshnessHours,
        columnStatsEnabled: res.columnStatsEnabled,
        refillEnabled: res.refillEnabled,
        refillSparseThreshold: res.refillSparseThreshold,
        verifySweepEnabled: res.verifySweepEnabled,
        autofanoutMaxChildrenPerRun: res.autofanoutMaxChildrenPerRun,
        autofanoutMaxConcurrentChildren: res.autofanoutMaxConcurrentChildren,
      });
      setCoverageMsg('Saved. Applies to new discovery and fingerprint runs, install-wide.');
    } catch (e) {
      setCoverageMsg(e?.message || 'Save failed');
    } finally {
      setSavingCoverage(false);
    }
  };

  // Persist the Test data subsets card; saveAdvancedConfig merges it over the stored config.
  const saveTdm = async () => {
    setSavingTdm(true);
    setTdmMsg(null);
    try {
      const res = await saveAdvancedConfig({
        tdmFanoutThreshold: tdmFanout !== null && String(tdmFanout).trim() !== '' ? Number(tdmFanout) : null,
      });
      setTdmFanout(res.tdmFanoutThreshold != null ? res.tdmFanoutThreshold : null);
      setTdmMsg('Saved. Applies to the next graph Test, preview and run.');
    } catch (e) {
      setTdmMsg(e?.message || 'Save failed');
    } finally {
      setSavingTdm(false);
    }
  };

  const setCoverageField = (key, value) => {
    setCoverageMsg(null);
    setCoverage((prev) => ({ ...prev, [key]: value }));
  };

  const setSmtpField = (key, value) => {
    setSmtpMsg(null);
    setSmtp((prev) => ({ ...prev, [key]: value }));
  };

  // Persist the SMTP card. saveAdvancedConfig merges these over the stored config (the
  // backend PUT is full-replace, so every other advanced-config field is round-tripped by
  // the helper). smtpPassword rides along ONLY when the admin typed one; smtpPasswordClear
  // only when they clicked Clear.
  const saveSmtp = async () => {
    setSavingSmtp(true);
    setSmtpMsg(null);
    try {
      const changes = {
        smtpEnabled: smtp.enabled,
        smtpHost: smtp.host.trim() === '' ? null : smtp.host.trim(),
        smtpPort: String(smtp.port).trim() === '' ? null : Number(smtp.port),
        smtpTlsMode: smtp.tlsMode,
        smtpUsername: smtp.username.trim() === '' ? null : smtp.username.trim(),
        smtpFromAddress: smtp.fromAddress.trim() === '' ? null : smtp.fromAddress.trim(),
      };
      if (smtpPassword !== '') changes.smtpPassword = smtpPassword;
      if (smtpClearPassword) changes.smtpPasswordClear = true;
      const res = await saveAdvancedConfig(changes);
      setSmtp(smtpStateFrom(res));
      if (res.smtpEnvDefaults && Object.keys(res.smtpEnvDefaults).length > 0) {
        setSmtpEnvDefaults(res.smtpEnvDefaults);
      }
      setSmtpPassword('');
      setSmtpClearPassword(false);
      setSmtpMsg('Saved. Blank/Default fields use the deployment env defaults.');
    } catch (e) {
      setSmtpMsg(e?.message || 'Save failed');
    } finally {
      setSavingSmtp(false);
    }
  };

  // Persist the email-domain policy. saveAdvancedConfig merges over the stored config and
  // always sends both arrays (full-replace PUT). The response carries the server-normalized
  // lists, which are rendered back so the admin sees what actually took effect.
  const saveDomains = async () => {
    setSavingDomains(true);
    setDomainsMsg(null);
    try {
      const res = await saveAdvancedConfig({
        emailDomainWhitelist: parseDomainList(domains.whitelist),
        emailDomainBlacklist: parseDomainList(domains.blacklist),
      });
      setDomains({
        whitelist: (res.emailDomainWhitelist || []).join('\n'),
        blacklist: (res.emailDomainBlacklist || []).join('\n'),
      });
      setDomainsMsg('Saved. Applies to new users and email changes; existing accounts are unaffected.');
    } catch (e) {
      setDomainsMsg(e?.message || 'Save failed');
    } finally {
      setSavingDomains(false);
    }
  };

  const sendTest = async () => {
    setSendingTest(true);
    setTestMsg(null);
    try {
      const res = await sendTestEmail(testTo.trim());
      setTestMsg({
        ok: true,
        text: res && res.warning ? `Sent. ${res.warning}` : `Test email sent to ${testTo.trim()}.`,
      });
    } catch (e) {
      // 422 -> { sent: false, error: "..." } — show the server's error text verbatim.
      setTestMsg({ ok: false, text: e?.body?.error || e?.message || 'Send failed' });
    } finally {
      setSendingTest(false);
    }
  };

  // Deployment default row cap, pulled from the read-only platform-config for reference.
  const deploymentRowCap = deploymentRowCapFrom(platformCfg);

  if (loading) return (<div><h1 className='adv-config-screen-title'>General Settings</h1><div>Loading…</div></div>);

  return (
    <div>
      <h1 className='adv-config-screen-title'>General Settings</h1>
      <p className='adv-config-screen-desc'>
        Platform knobs that can be changed at runtime. Values apply install-wide.
      </p>
      {error && <div className='adv-config-msg-err' style={{ marginBottom: 16 }}>{error}</div>}

      <div className='adv-config-card'>
        <h2 style={{ fontSize: 15, margin: '0 0 4px' }}>Landscape Refresh</h2>
        <p style={{ fontSize: 12, color: INK[500], margin: '0 0 12px' }}>
          How often c2-manager re-scans your cloud infrastructure to refresh the resource
          landscape. Defaults to every 60 minutes.
        </p>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type='checkbox' checked={disc.enabled} disabled={!canEdit || savingDisc}
              onChange={(e) => { setDiscMsg(null); setDisc({ ...disc, enabled: e.target.checked }); }} />
            Enabled
          </label>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>
              Interval (minutes, default: 60, {disc.minIntervalMinutes} minute minimum)
            </div>
            <input type='number' className='adv-config-search' style={{ minWidth: 140 }}
              min={disc.minIntervalMinutes} value={disc.intervalMinutes} disabled={!canEdit || savingDisc}
              onChange={(e) => { setDiscMsg(null); setDisc({ ...disc, intervalMinutes: e.target.value }); }} />
          </label>
          <button type='button' className='adv-config-btn' onClick={saveDiscovery}
            disabled={!canEdit || savingDisc}>
            {savingDisc ? 'Saving…' : 'Save'}
          </button>
          {discMsg && <span className='adv-config-msg-ok'>{discMsg}</span>}
        </div>
      </div>

      <DefaultPortsCard canEdit={canEdit} />

      <div className='adv-config-card' data-testid='profile-toggle-card'>
        <h2 style={{ fontSize: 15, margin: '0 0 4px' }}>Fingerprint / Schema Intelligence</h2>
        <p style={{ fontSize: 12, color: INK[500], margin: '0 0 12px' }}>
          Enables the Schema Intelligence feature: cross-source schema identities, similarity
          matching, and commercial-schema detection. When enabled, the “Schema Intelligence” item
          appears in the left navigation. Applies install-wide.
        </p>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type='checkbox' data-testid='profile-toggle' checked={profileEnabled}
              disabled={!canEdit || savingProfile}
              onChange={(e) => { setProfileMsg(null); saveProfile(e.target.checked); }} />
            Enabled
          </label>
          {savingProfile && <span style={{ fontSize: 12, color: INK[500] }}>Saving…</span>}
          {profileMsg && <span className='adv-config-msg-ok'>{profileMsg}</span>}
        </div>
      </div>

      <div className='adv-config-card' data-testid='import-export-card'>
        <h2 style={{ fontSize: 15, margin: '0 0 4px' }}>Data source import / export</h2>
        <p style={{ fontSize: 12, color: INK[500], margin: '0 0 12px' }}>
          Shows the Import / Export actions on the Data Sources page. These bulk-edit the source
          list, so they are hidden by default and — even when enabled here — appear only to admin
          users. Enable only if your team needs to move data-source definitions between installs.
        </p>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type='checkbox' data-testid='import-export-toggle' checked={importExportEnabled}
              disabled={!canEdit || savingImportExport}
              onChange={(e) => { setImportExportMsg(null); saveImportExport(e.target.checked); }} />
            Enabled (admin only)
          </label>
          {savingImportExport && <span style={{ fontSize: 12, color: INK[500] }}>Saving…</span>}
          {importExportMsg && <span className='adv-config-msg-ok'>{importExportMsg}</span>}
        </div>
      </div>

      <div className='adv-config-card' data-testid='smtp-card'>
        <h2 style={{ fontSize: 15, margin: '0 0 4px' }}>Email (SMTP)</h2>
        <p style={{ fontSize: 12, color: INK[500], margin: '0 0 12px' }}>
          Outbound email for password-reset links and user invites. Values set here override
          the deployment environment defaults shown under each field; blank / “Default”
          fields fall back to those defaults. Applies install-wide.
        </p>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Enabled</div>
            <select className='adv-config-search' style={{ minWidth: 150 }}
              data-testid='smtp-enabled'
              value={smtp.enabled == null ? '' : String(smtp.enabled)}
              disabled={!canEdit || savingSmtp}
              onChange={(e) => setSmtpField('enabled',
                e.target.value === '' ? null : e.target.value === 'true')}>
              <option value=''>{`Default (${smtpEnvDefaults.enabled ? 'on' : 'off'})`}</option>
              <option value='true'>On</option>
              <option value='false'>Off</option>
            </select>
          </label>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Host</div>
            <input type='text' className='adv-config-search' style={{ minWidth: 200 }}
              data-testid='smtp-host'
              value={smtp.host} disabled={!canEdit || savingSmtp}
              onChange={(e) => setSmtpField('host', e.target.value)} />
            <EnvDefaultHint value={smtpEnvDefaults.host} />
          </label>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Port</div>
            <input type='number' className='adv-config-search' style={{ minWidth: 110 }}
              data-testid='smtp-port'
              min={1} max={65535} value={smtp.port} disabled={!canEdit || savingSmtp}
              onChange={(e) => setSmtpField('port', e.target.value)} />
            <EnvDefaultHint value={smtpEnvDefaults.port} />
          </label>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>TLS mode</div>
            <select className='adv-config-search' style={{ minWidth: 140 }}
              data-testid='smtp-tls-mode'
              value={smtp.tlsMode == null ? '' : smtp.tlsMode}
              disabled={!canEdit || savingSmtp}
              onChange={(e) => setSmtpField('tlsMode', e.target.value === '' ? null : e.target.value)}>
              <option value=''>Default</option>
              <option value='starttls'>starttls</option>
              <option value='implicit'>implicit</option>
              <option value='none'>none</option>
            </select>
            <EnvDefaultHint value={smtpEnvDefaults.tlsMode} />
          </label>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Username</div>
            <input type='text' className='adv-config-search' style={{ minWidth: 180 }}
              data-testid='smtp-username'
              value={smtp.username} disabled={!canEdit || savingSmtp}
              onChange={(e) => setSmtpField('username', e.target.value)} />
            <EnvDefaultHint value={smtpEnvDefaults.username} />
          </label>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Password</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type='password' className='adv-config-search' style={{ minWidth: 180 }}
                data-testid='smtp-password'
                value={smtpPassword}
                placeholder={smtpClearPassword
                  ? '(will be cleared)'
                  : (smtp.passwordSet ? '(unchanged)' : '(not set)')}
                disabled={!canEdit || savingSmtp || smtpClearPassword}
                onChange={(e) => { setSmtpMsg(null); setSmtpPassword(e.target.value); }} />
              {smtp.passwordSet && !smtpClearPassword && canEdit && (
                <button type='button' className='adv-config-btn-secondary adv-config-btn'
                  data-testid='smtp-password-clear'
                  onClick={() => { setSmtpMsg(null); setSmtpPassword(''); setSmtpClearPassword(true); }}
                  disabled={savingSmtp}>
                  Clear
                </button>
              )}
            </div>
            {smtpClearPassword && (
              <div style={{ fontSize: 11, color: INK[500], marginTop: 4 }}>
                Stored password will be cleared on save.{' '}
                <button type='button' className='adv-config-btn-secondary adv-config-btn'
                  onClick={() => setSmtpClearPassword(false)} disabled={savingSmtp}>
                  Undo
                </button>
              </div>
            )}
            {!smtpClearPassword && smtpEnvDefaults.passwordSet && (
              <div style={{ fontSize: 11, color: INK[500], marginTop: 4 }}>
                Default: set from deployment env
              </div>
            )}
          </label>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>From address</div>
            <input type='text' className='adv-config-search' style={{ minWidth: 200 }}
              data-testid='smtp-from'
              value={smtp.fromAddress} disabled={!canEdit || savingSmtp}
              onChange={(e) => setSmtpField('fromAddress', e.target.value)} />
            <EnvDefaultHint value={smtpEnvDefaults.from} />
          </label>
          <button type='button' className='adv-config-btn' onClick={saveSmtp}
            disabled={!canEdit || savingSmtp} style={{ alignSelf: 'flex-end' }}>
            {savingSmtp ? 'Saving…' : 'Save'}
          </button>
          {smtpMsg && <span className='adv-config-msg-ok' style={{ alignSelf: 'flex-end' }}>{smtpMsg}</span>}
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 16 }}>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Send test email to</div>
            <input type='email' className='adv-config-search' style={{ minWidth: 220 }}
              data-testid='smtp-test-to'
              value={testTo} disabled={!canEdit || sendingTest}
              onChange={(e) => { setTestMsg(null); setTestTo(e.target.value); }} />
          </label>
          <button type='button' className='adv-config-btn' onClick={sendTest}
            data-testid='smtp-test-send'
            disabled={!canEdit || sendingTest || testTo.trim() === ''}>
            {sendingTest ? 'Sending…' : 'Send test email'}
          </button>
          {testMsg && (
            <span className={testMsg.ok ? 'adv-config-msg-ok' : 'adv-config-msg-err'}>
              {testMsg.text}
            </span>
          )}
        </div>
      </div>

      <div className='adv-config-card' data-testid='email-domains-card'>
        <h2 style={{ fontSize: 15, margin: '0 0 4px' }}>User email domains</h2>
        <p style={{ fontSize: 12, color: INK[500], margin: '0 0 12px' }}>
          Account policy for which email domains may be used when creating users or changing
          a user’s email. Enforced when saving the user, not retroactively; the initial-admin
          setup is exempt. Blocked domains win over allowed ones. Entries are normalized on
          save (lowercased, leading “@” stripped, duplicates dropped).
        </p>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Allowed domains (whitelist)</div>
            <textarea className='adv-config-search' rows={5}
              style={{ minWidth: 240, resize: 'vertical' }}
              data-testid='email-domain-whitelist'
              value={domains.whitelist}
              disabled={!canEdit || savingDomains}
              onChange={(e) => { setDomainsMsg(null); setDomains({ ...domains, whitelist: e.target.value }); }} />
            <div style={{ fontSize: 11, color: INK[500], marginTop: 4 }}>
              Empty = any domain accepted. One domain per line, e.g. corp.com
            </div>
          </label>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Blocked domains (blacklist)</div>
            <textarea className='adv-config-search' rows={5}
              style={{ minWidth: 240, resize: 'vertical' }}
              data-testid='email-domain-blacklist'
              value={domains.blacklist}
              disabled={!canEdit || savingDomains}
              onChange={(e) => { setDomainsMsg(null); setDomains({ ...domains, blacklist: e.target.value }); }} />
            <div style={{ fontSize: 11, color: INK[500], marginTop: 4 }}>
              These domains are always rejected, even if whitelisted.
            </div>
          </label>
          <button type='button' className='adv-config-btn' onClick={saveDomains}
            disabled={!canEdit || savingDomains}>
            {savingDomains ? 'Saving…' : 'Save'}
          </button>
          {domainsMsg && <span className='adv-config-msg-ok'>{domainsMsg}</span>}
        </div>
      </div>

      <div className='adv-config-card'>
        <h2 style={{ fontSize: 15, margin: '0 0 4px' }}>Sampling — max rows per table (global)</h2>
        <p style={{ fontSize: 12, color: INK[500], margin: '0 0 12px' }}>
          A single install-wide cap on how many rows discovery samples from each table. Applies to
          every discovery run and fully overrides the deployment default. Pre-filled with the
          current deployment default; edit and Save to override, or Clear to fall back to the
          deployment default. Set low (e.g. 100) for fast laptop/dev demos, then raise it for real
          runs.
        </p>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>
              Max rows / table{deploymentRowCap ? ` (deployment default: ${deploymentRowCap})` : ''}
            </div>
            <input type='number' className='adv-config-search' style={{ minWidth: 200 }}
              min={1} placeholder='deployment default' value={maxRows}
              disabled={!canEdit || savingMax}
              onChange={(e) => { setMaxMsg(null); setMaxRows(e.target.value); }} />
          </label>
          <button type='button' className='adv-config-btn' onClick={saveMaxRows}
            disabled={!canEdit || savingMax}>
            {savingMax ? 'Saving…' : 'Save'}
          </button>
          {maxRows !== '' && canEdit && (
            <button type='button' className='adv-config-btn-secondary adv-config-btn'
              onClick={() => { setMaxMsg(null); setMaxRows(''); }} disabled={savingMax}>
              Clear
            </button>
          )}
          {maxMsg && <span className='adv-config-msg-ok'>{maxMsg}</span>}
        </div>
      </div>

      <div className='adv-config-card'>
        <h2 style={{ fontSize: 15, margin: '0 0 4px' }}>
          Scan inside embedded JSON / XML column values
        </h2>
        <p style={{ fontSize: 12, color: INK[500], margin: '0 0 12px' }}>
          Some columns hold a whole JSON or XML document as their value — common in event and
          telemetry tables. When on, discovery reads the keys inside those values as field names,
          so a <code>{'{"dob":"1979-03-14"}'}</code> value in a column named <code>event</code> is
          reported as a birth date at <code>event.dob</code> rather than being missed. When off,
          the value is still scanned as plain text — emails and phone numbers are still found, but
          anything that depends on the key name is not.
        </p>
        <p style={{ fontSize: 12, color: INK[500], margin: '0 0 12px' }}>
          Turning this on adds extra model requests for tables that contain such columns, so turn
          it off if a large install has cost or run-time trouble. Applies to <strong>database
          discovery runs</strong>; file-source runs follow the deployment default.
        </p>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Embedded scan</div>
            <select
              className='adv-config-search'
              style={{ minWidth: 240 }}
              value={scanEmbedded == null ? '' : String(scanEmbedded)}
              disabled={!canEdit || savingEmbedded}
              onChange={(e) => {
                setEmbeddedMsg(null);
                const v = e.target.value;
                saveScanEmbedded(v === '' ? null : v === 'true');
              }}
            >
              <option value=''>Default (on)</option>
              <option value='true'>On — scan inside embedded values</option>
              <option value='false'>Off — plain text only</option>
            </select>
          </label>
          {savingEmbedded && <span style={{ fontSize: 12, color: INK[500] }}>Saving…</span>}
          {embeddedMsg && <span className='adv-config-msg-ok'>{embeddedMsg}</span>}
        </div>
      </div>

      <div className='adv-config-card' data-testid='discovery-coverage-card'>
        <h2 style={{ fontSize: 15, margin: '0 0 4px' }}>Discovery coverage</h2>
        <p style={{ fontSize: 12, color: INK[500], margin: '0 0 12px' }}>
          Controls how hard discovery works to reach data the random sample misses. Before a
          discovery run, a fresh fingerprint is taken when the newest one is older than the freshness
          window; the fingerprint records how populated each column is (null counts and distinct
          values), and discovery uses that to re-query sparsely populated columns the sample came
          back empty on. All values apply install-wide; blank/Default defers to the deployment
          default.
        </p>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Fingerprint freshness (hours, default: 48)</div>
            <input type='number' className='adv-config-search' style={{ minWidth: 140 }}
              min={1} placeholder='48'
              value={coverage.profileFreshnessHours != null ? coverage.profileFreshnessHours : ''}
              disabled={!canEdit || savingCoverage}
              onChange={(e) => setCoverageField('profileFreshnessHours',
                e.target.value === '' ? null : e.target.value)} />
          </label>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Fingerprint column stats</div>
            <select className='adv-config-search' style={{ minWidth: 150 }}
              value={coverage.columnStatsEnabled == null ? '' : String(coverage.columnStatsEnabled)}
              disabled={!canEdit || savingCoverage}
              onChange={(e) => setCoverageField('columnStatsEnabled',
                e.target.value === '' ? null : e.target.value === 'true')}>
              <option value=''>Default (on)</option>
              <option value='true'>On</option>
              <option value='false'>Off</option>
            </select>
          </label>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Sparse-column refill</div>
            <select className='adv-config-search' style={{ minWidth: 150 }}
              value={coverage.refillEnabled == null ? '' : String(coverage.refillEnabled)}
              disabled={!canEdit || savingCoverage}
              onChange={(e) => setCoverageField('refillEnabled',
                e.target.value === '' ? null : e.target.value === 'true')}>
              <option value=''>Default (on)</option>
              <option value='true'>On</option>
              <option value='false'>Off</option>
            </select>
          </label>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Sparse threshold (values, default: 10)</div>
            <input type='number' className='adv-config-search' style={{ minWidth: 140 }}
              min={1} placeholder='10'
              value={coverage.refillSparseThreshold != null ? coverage.refillSparseThreshold : ''}
              disabled={!canEdit || savingCoverage}
              onChange={(e) => setCoverageField('refillSparseThreshold',
                e.target.value === '' ? null : e.target.value)} />
          </label>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Verify sweep (post-discovery)</div>
            <select className='adv-config-search' style={{ minWidth: 150 }}
              value={coverage.verifySweepEnabled == null ? '' : String(coverage.verifySweepEnabled)}
              disabled={!canEdit || savingCoverage}
              onChange={(e) => setCoverageField('verifySweepEnabled',
                e.target.value === '' ? null : e.target.value === 'true')}>
              <option value=''>Default (on)</option>
              <option value='true'>On</option>
              <option value='false'>Off</option>
            </select>
          </label>
          {/* "Discover everything" fan-out caps. Blank = deployment default. Lowering the
              child cap never loses a candidate — the overflow stays a manual Discover row. */}
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>
              Auto sub-sources per folder search (default: 25)
            </div>
            <input type='number' className='adv-config-search' style={{ minWidth: 140 }}
              min={1} placeholder='25'
              data-testid='autofanout-max-children'
              value={coverage.autofanoutMaxChildrenPerRun != null
                ? coverage.autofanoutMaxChildrenPerRun : ''}
              disabled={!canEdit || savingCoverage}
              onChange={(e) => setCoverageField('autofanoutMaxChildrenPerRun',
                e.target.value === '' ? null : e.target.value)} />
          </label>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>
              Concurrent auto sub-discoveries (default: 3)
            </div>
            <input type='number' className='adv-config-search' style={{ minWidth: 140 }}
              min={1} placeholder='3'
              data-testid='autofanout-max-concurrent'
              value={coverage.autofanoutMaxConcurrentChildren != null
                ? coverage.autofanoutMaxConcurrentChildren : ''}
              disabled={!canEdit || savingCoverage}
              onChange={(e) => setCoverageField('autofanoutMaxConcurrentChildren',
                e.target.value === '' ? null : e.target.value)} />
          </label>
          <button type='button' className='adv-config-btn' onClick={saveCoverage}
            disabled={!canEdit || savingCoverage}>
            {savingCoverage ? 'Saving…' : 'Save'}
          </button>
          {coverageMsg && <span className='adv-config-msg-ok'>{coverageMsg}</span>}
        </div>
      </div>

      <div className='adv-config-card' data-testid='tdm-settings-card'>
        <h2 style={{ fontSize: 15, margin: '0 0 4px' }}>Test data subsets</h2>
        <p style={{ fontSize: 12, color: INK[500], margin: '0 0 14px' }}>
          The isolation report of a data graph Test, a subset preview and a run flags a rule that returns more
          rows than this for one source value (FANOUT). It only informs: nothing is stopped or capped.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: INK[500] }}>Fan-out threshold, rows per value (default: 1,000)</div>
            <input type='number' className='adv-config-search' style={{ minWidth: 140 }}
              min={1} placeholder='1000'
              data-testid='tdm-fanout-threshold'
              value={tdmFanout != null ? tdmFanout : ''}
              disabled={!canEdit || savingTdm}
              onChange={(e) => { setTdmMsg(null); setTdmFanout(e.target.value === '' ? null : e.target.value); }} />
          </label>
          <button type='button' className='adv-config-btn' onClick={saveTdm} disabled={!canEdit || savingTdm}
            data-testid='tdm-settings-save'>
            {savingTdm ? 'Saving…' : 'Save'}
          </button>
          {tdmMsg && <span className='adv-config-msg-ok' data-testid='tdm-settings-msg'>{tdmMsg}</span>}
        </div>
      </div>

      <div className='adv-config-card'>
        <h2 style={{ fontSize: 15, margin: '0 0 4px' }}>Platform configuration (read-only)</h2>
        <p style={{ fontSize: 12, color: INK[500], margin: '0 0 14px' }}>
          Current effective values of platform knobs set in deployment YAML/env. Shown for
          visibility; not editable here yet. Change these via deployment config, or ask to have a
          specific one made runtime-editable.
        </p>
        <ReadOnlyConfigTable title='Platform API' items={platformCfg.platform} />
        <ReadOnlyConfigTable title='Manager' items={platformCfg.manager} />
        {platformCfg.platform.length === 0 && platformCfg.manager.length === 0 && (
          <div style={{ color: INK[500], fontSize: 13 }}>No platform config values available.</div>
        )}
      </div>
    </div>
  );
};

// Known database ports used by c2-manager's landscape port classification. Canonical rows
// are the built-in engine defaults (undeletable); custom rows are the site's nonstandard
// ports and classify as EXACT matches on the next landscape scan. Row-based CRUD against
// c2-manager directly — no sibling round-tripping like the advanced-config singleton.
const DefaultPortsCard = ({ canEdit }) => {
  const [rows, setRows] = useState([]);
  const [engines, setEngines] = useState([]);
  const [portWindow, setPortWindow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [newEngine, setNewEngine] = useState('');
  const [newPort, setNewPort] = useState('');
  const [saving, setSaving] = useState(false);

  const applyResponse = (res) => {
    setRows(Array.isArray(res?.ports) ? res.ports : []);
    setEngines(Array.isArray(res?.engines) ? res.engines : []);
    setPortWindow(res?.portWindow != null ? res.portWindow : null);
  };

  useEffect(() => {
    let alive = true;
    getDbPortDefaults()
      .then((res) => {
        if (!alive) return;
        applyResponse(res);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e?.body?.message || e?.message || 'Failed to load port defaults');
        setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  const reload = async () => applyResponse(await getDbPortDefaults());

  const addRow = async () => {
    setSaving(true);
    setMsg(null);
    setErr(null);
    try {
      await addDbPortDefault(newEngine.trim().toLowerCase(), Number(newPort));
      await reload();
      setMsg(`Added ${newEngine.trim().toLowerCase()} on port ${newPort}. Applies on the next landscape scan.`);
      setNewEngine('');
      setNewPort('');
    } catch (e) {
      setErr(e?.body?.message || e?.message || 'Add failed');
    } finally {
      setSaving(false);
    }
  };

  const removeRow = async (row) => {
    setSaving(true);
    setMsg(null);
    setErr(null);
    try {
      await deleteDbPortDefault(row.id);
      await reload();
      setMsg(`Removed port ${row.port}.`);
    } catch (e) {
      setErr(e?.body?.message || e?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const canAdd = canEdit && !saving && newEngine.trim() !== '' && String(newPort).trim() !== '';

  return (
    <div className='adv-config-card' data-testid='default-ports-card'>
      <h2 style={{ fontSize: 15, margin: '0 0 4px' }}>Database ports</h2>
      <p style={{ fontSize: 12, color: INK[500], margin: '0 0 12px' }}>
        The ports the cloud landscape scanner recognizes as database ports. Built-in defaults are
        always kept. If your site runs an engine on a nonstandard port fleet-wide (e.g. PostgreSQL
        on 6543), add it here — custom ports are treated as exact matches when classifying open
        ports in the cloud landscape{portWindow != null ? `, and ports up to ${portWindow} above any listed port classify as "nearby"` : ''}.
      </p>
      {loading ? (
        <div style={{ fontSize: 13, color: INK[500] }}>Loading…</div>
      ) : (
        <>
          <table className='adv-config-table' style={{ maxWidth: 560 }}>
            <thead>
              <tr>
                <th style={{ width: '40%' }}>Engine</th>
                <th style={{ width: 120 }}>Port</th>
                <th style={{ width: 120 }}>Type</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.engine}</td>
                  <td className='adv-config-mono'>{row.port}</td>
                  <td style={{ fontSize: 12, color: row.canonical ? INK[500] : undefined }}>
                    {row.canonical ? 'built-in' : 'custom'}
                  </td>
                  <td>
                    {!row.canonical && canEdit && (
                      <button
                        type='button'
                        className='adv-config-btn-secondary adv-config-btn'
                        onClick={() => removeRow(row)}
                        disabled={saving}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {canEdit && (
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 12 }}>
              <label style={{ fontSize: 13 }}>
                <div style={{ marginBottom: 4, color: INK[500] }}>Engine</div>
                <select
                  className='adv-config-search'
                  style={{ minWidth: 170 }}
                  value={newEngine}
                  disabled={saving}
                  onChange={(e) => { setMsg(null); setErr(null); setNewEngine(e.target.value); }}
                >
                  <option value=''>Select engine…</option>
                  {engines.map((engine) => (
                    <option key={engine} value={engine}>{engine}</option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: 13 }}>
                <div style={{ marginBottom: 4, color: INK[500] }}>Port</div>
                <input
                  type='number'
                  className='adv-config-search'
                  style={{ minWidth: 120 }}
                  min={1}
                  max={65535}
                  value={newPort}
                  disabled={saving}
                  onChange={(e) => { setMsg(null); setErr(null); setNewPort(e.target.value); }}
                />
              </label>
              <button type='button' className='adv-config-btn' onClick={addRow} disabled={!canAdd}>
                {saving ? 'Saving…' : 'Add'}
              </button>
            </div>
          )}
          {msg && <div className='adv-config-msg-ok' style={{ marginTop: 8 }}>{msg}</div>}
          {err && <div className='adv-config-msg-err' style={{ marginTop: 8 }}>{err}</div>}
        </>
      )}
    </div>
  );
};

// Compact read-only label/value/description table for one service's config knobs.
const ReadOnlyConfigTable = ({ title, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: INK[500], textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
        {title}
      </div>
      <table className='adv-config-table'>
        <thead>
          <tr>
            <th style={{ width: '32%' }}>Setting</th>
            <th style={{ width: 160 }}>Value</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={`${title}-${i}`}>
              <td>{it.label}</td>
              <td className='adv-config-mono'>{String(it.value)}</td>
              <td style={{ color: INK[500], fontSize: 12 }}>{it.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SettingsScreen;
